import {
  AutopilotTaskDto,
  TaskBlocker,
  TaskBlockerBlockerType,
  TaskCategoryTaskType,
} from '@urbanx/agx.activities.client';
import {
  AgxButton,
  AgxEmptyState,
  AgxGlyph,
  AgxSpacer,
  AgxTypography,
} from '@urbanx/agx.ui-components2';
import { ComponentType, JSX, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { LaunchApprovalBlocker } from './LaunchApprovalBlocker';
import MarkedAsExchange from './BlockerModals/MarkedAsExchange/MarkedAsExchange';
import { useMarkedAsExchange } from './BlockerModals/MarkedAsExchange/useMarkedAsExchange';

interface BlockerLayoutProps {
  task: AutopilotTaskDto;
  title?: string;
  description?: string;
  children?: ReactNode;
  onTasksInvalidated?: () => void | Promise<void>;
}

interface BlockerDisplayItem {
  title?: string;
  description?: string;
  buttonText: string;
  taskLink?: string;
  buttonAction?: () => void;
  modalComponent?: ComponentType<BlockerModalProps>;
  rowComponent?: ComponentType<BlockerRowProps>;
  blocker: TaskBlocker;
}

interface BlockerModalProps {
  controller: ReturnType<typeof useMarkedAsExchange>;
}

const blockerCopyByType: Record<
  TaskBlockerBlockerType,
  Omit<BlockerDisplayItem, 'taskLink' | 'blocker'>
> = {
  [TaskBlockerBlockerType.InitialDepositTask]: {
    title: 'Initial Deposit Outstanding',
    description:
      'Once the Initial Deposit is paid, you will be able to complete the Unconditional task.',
    buttonText: 'Go to Deposits',
  },
  [TaskBlockerBlockerType.BalanceDepositTask]: {
    title: 'Balance Deposit Outstanding',
    description:
      'Once the Balance Deposit is paid, you will be able to complete the Unconditional task.',
    buttonText: 'Go to Deposits',
  },
  [TaskBlockerBlockerType.ContractUploadedStatusDraft]: {
    title: 'Contract Uploaded Status Draft',
    description: 'Awaiting Final Contract from Solicitor',
    buttonText: 'Upload Final Contract',
  },
  [TaskBlockerBlockerType.CommissionShortfallConfirmation]: {
    title: 'Commission Shortfall',
    description: 'There is insufficient deposit funds to cover the full amount of the commission',
    buttonText: 'Go to other tasks',
  },
  [TaskBlockerBlockerType.ReferralInvoiceOutstanding]: {
    title: 'Referral / Conjunction Invoice Outstanding',
    description: 'Complete the Referral / Conjunction Invoice task to progress Settlement Payments',
    buttonText: 'Go to Referral / Conjunction',
  },
  [TaskBlockerBlockerType.CommissionPlanTypeVendorChoice]: {
    title: 'Commission is Vendor Choice',
    description:
      'Once the Commission Type is updated, you will be able to complete the Unconditional task.',
    buttonText: 'Go to Commission',
  },
  [TaskBlockerBlockerType.LaunchDateNotSet]: {
    title: 'Launch Date Not Set',
    description: 'Update the Launch Date to progress the task',
    buttonText: 'Go to Launch Details',
  },
  [TaskBlockerBlockerType.ApprovalReceivedNotConfirmed]: {
    title: 'Awaiting Agent Approval',
    description: 'Once approval is received, you can confirm it to progress the launch task.',
    buttonText: 'Confirm Approval',
    rowComponent: LaunchApprovalBlockerRow,
  },
  [TaskBlockerBlockerType.MarkedAsExchanged]: {
    title: 'Pending Confirmation of Exchange',
    buttonText: 'Mark as Exchanged',
    modalComponent: MarkedAsExchange,
    rowComponent: MarkedAsExchangeBlockerRow,
  },
  [TaskBlockerBlockerType.TerminationTaskBuyerBankDetails]: {
    title: 'Buyer Bank Details Outstanding',
    description: 'Once Buyer Bank Details are complete, you can complete Termination Payments',
    buttonText: 'Go to Tasks',
  },
  [TaskBlockerBlockerType.WithdrawnOfferBuyerBankDetails]: {
    title: 'Buyer Bank Details Outstanding',
    description: 'Once Buyer Bank Details are complete, you can complete Withdrawn Offer Payments.',
    buttonText: 'Go to Tasks',
  },
  [TaskBlockerBlockerType.TerminationTaskVendorBankDetails]: {
    title: 'Vendor Bank Details still outstanding',
    description: 'Once Vendor Bank Details are complete, you can complete Termination Payments',
    buttonText: 'Go to Tasks',
    rowComponent: LockedBlockerRow,
  },
  [TaskBlockerBlockerType.WithdrawnTaskVendorBankDetails]: {
    title: 'Finalise Vendor Bank Details',
    description: 'Complete the task before continuing to Withdrawn Payments.',
    buttonText: 'Go to Task',
  },
  [TaskBlockerBlockerType.VendorBankDetailsOutstanding]: {
    title: 'Vendor Bank Details still outstanding',
    description:
      'Once Vendor Bank Details task complete, you can complete Settlement Payments task.',
    buttonText: 'Go to Other Tasks',
  },
  [TaskBlockerBlockerType.NotMarkedAsSettled]: {
    title: 'Pending Settlement Confirmation',
    buttonText: 'Mark as Settled',
  },
  [TaskBlockerBlockerType.ContractDateNotSet]: {
    title: 'NOT IMPLEMENTED',
    description: 'NOT IMPLEMENTED',
    buttonText: 'NOT IMPLEMENTED',
  },
};

export const BlockerLayout = ({
  task,
  title,
  description,
  children,
  onTasksInvalidated,
}: BlockerLayoutProps): JSX.Element => {
  const blockerItems = (task.blockers ?? [])
    .map((blocker) => mapBlockerToDisplayItem(task, blocker))
    .filter(isBlockerDisplayItem);

  const shouldRenderDefaultBlockerContent = children == null;
  const showFallbackContent = blockerItems.length === 0 && (title || description);
  const showSingleBlockerGlyph = blockerItems.length === 1;

  return (
    <AgxSpacer
      fill
      direction="vertical"
      contentJustification="center"
      contentAlignment="center"
      gap="lg"
    >
      <AgxSpacer
        direction="vertical"
        fill
        contentAlignment="center"
        gap="lg"
        padding={{ left: 'spacing-8xl', right: 'spacing-8xl' }}
      >
        {children}
        {shouldRenderDefaultBlockerContent && showFallbackContent && (
          <AgxSpacer direction="vertical" contentAlignment="center" gap="sm">
            {title && (
              <AgxTypography
                id="pendingTitle"
                type="heading"
                size="small"
                color="var(--text-primary)"
              >
                {title}
              </AgxTypography>
            )}
            {description && (
              <AgxTypography
                id="pendingDescription"
                type="body_ui"
                size="small"
                color="var(--text-secondary)"
              >
                {description}
              </AgxTypography>
            )}
          </AgxSpacer>
        )}
        {shouldRenderDefaultBlockerContent && blockerItems.length === 0 && (
          <AgxSpacer>
            <AgxEmptyState
              variant="advanced"
              label="Assign task to unlock"
              glyphType={'locked'}
              actionButtons={[]}
            ></AgxEmptyState>
          </AgxSpacer>
        )}
        {shouldRenderDefaultBlockerContent && blockerItems.length > 0 && (
          <AgxSpacer
            direction="vertical"
            fill
            contentAlignment="center"
            contentJustification="center"
            gap="spacing-4xl"
          >
            {blockerItems.map((item, index) => {
              const RowComponent = item.rowComponent ?? BlockerRow;

              return (
                <RowComponent
                  key={`blocker-row-${index}`}
                  item={item}
                  task={task}
                  onTasksInvalidated={onTasksInvalidated}
                  showGlyph={showSingleBlockerGlyph}
                />
              );
            })}
          </AgxSpacer>
        )}
      </AgxSpacer>
    </AgxSpacer>
  );
};

interface BlockerRowProps {
  item: BlockerDisplayItem;
  task: AutopilotTaskDto;
  onTasksInvalidated?: () => void | Promise<void>;
  showGlyph?: boolean;
}

const BlockerRow = ({ item, showGlyph }: BlockerRowProps) => {
  const navigate = useNavigate();
  const taskLink = item.taskLink;

  return (
    <AgxSpacer direction="vertical" contentAlignment="center" gap="md" fill>
      {showGlyph && <AgxGlyph type="locked" height={110} width={110} />}

      {item.title && (
        <AgxTypography
          id="pendingTitle"
          type="heading"
          size={showGlyph ? 'x-large' : 'large'}
          color="var(--text-primary)"
        >
          {item.title}
        </AgxTypography>
      )}

      {item.description && (
        <AgxTypography
          id="pendingDescription"
          type="body_ui"
          size="small"
          color="var(--text-secondary)"
        >
          {item.description}
        </AgxTypography>
      )}

      {item.buttonText ? (
        <AgxButton
          id="pendingTaskButton"
          text={item.buttonText}
          variant="primary"
          size="large"
          onClick={() => (taskLink ? navigate(taskLink) : item.buttonAction?.())}
        />
      ) : null}
    </AgxSpacer>
  );
};

function LockedBlockerRow({ item }: BlockerRowProps) {
  const navigate = useNavigate();

  return (
    <AgxSpacer
      direction="vertical"
      contentAlignment="center"
      gap="spacing-lg"
      style={{ maxWidth: 420, textAlign: 'center' }}
    >
      <AgxGlyph type="locked" height={96} width={96} />
      <AgxSpacer direction="vertical" contentAlignment="center" gap="spacing-sm">
        {item.title && (
          <AgxTypography
            id="pendingTitle"
            type="body_ui_strong"
            size="medium"
            color="var(--text-primary)"
          >
            {item.title}
          </AgxTypography>
        )}
        {item.description && (
          <AgxTypography
            id="pendingDescription"
            type="body_ui"
            size="small"
            color="var(--text-secondary)"
          >
            {item.description}
          </AgxTypography>
        )}
      </AgxSpacer>
      {item.buttonText ? (
        <AgxButton
          id="pendingTaskButton"
          text={item.buttonText}
          variant="primary"
          size="large"
          onClick={() => (item.taskLink ? navigate(item.taskLink) : item.buttonAction?.())}
        />
      ) : null}
    </AgxSpacer>
  );
}

function MarkedAsExchangeBlockerRow({ item, task, showGlyph }: BlockerRowProps) {
  const controller = useMarkedAsExchange({ task });
  const ModalComponent = item.modalComponent;
  const itemWithAction = {
    ...item,
    buttonAction: controller.openModal,
  };

  return (
    <>
      <BlockerRow item={itemWithAction} task={task} showGlyph={showGlyph} />
      {ModalComponent ? <ModalComponent controller={controller} /> : null}
    </>
  );
}

function LaunchApprovalBlockerRow({ task, onTasksInvalidated }: BlockerRowProps) {
  if (!task.campaignId) return null;

  return (
    <LaunchApprovalBlocker
      campaignId={task.campaignId}
      contractId={task.contractId}
      onTasksInvalidated={onTasksInvalidated}
    />
  );
}

const mapBlockerToDisplayItem = (
  task: AutopilotTaskDto,
  blocker: TaskBlocker
): BlockerDisplayItem | null => {
  const blockerType = blocker.blockerType;
  if (!blockerType || !(blockerType in blockerCopyByType)) {
    return null;
  }

  const mappedCopy = blockerCopyByType[blockerType];
  const markedAsExchangedOverrides =
    blockerType === TaskBlockerBlockerType.MarkedAsExchanged &&
    task.category?.taskType === TaskCategoryTaskType.AgentExchangeInformation
      ? {
          title: 'Awaiting Final Contract from Solicitor',
          buttonText: 'Upload Final Contract',
        }
      : undefined;

  return {
    title: markedAsExchangedOverrides?.title ?? mappedCopy.title,
    description: mappedCopy.description,
    buttonText: markedAsExchangedOverrides?.buttonText ?? mappedCopy.buttonText,
    taskLink: blocker.taskLink,
    buttonAction: mappedCopy.buttonAction,
    modalComponent: mappedCopy.modalComponent,
    rowComponent: mappedCopy.rowComponent,
    blocker,
  };
};

const isBlockerDisplayItem = (item: BlockerDisplayItem | null): item is BlockerDisplayItem =>
  item !== null;
